import React, { useState, useEffect, useCallback } from 'react'
import { token } from '@atlaskit/tokens'
import Button from '@atlaskit/button'
import Spinner from '@atlaskit/spinner'
import Tooltip from '@atlaskit/tooltip'
import AttachmentIcon from '@atlaskit/icon/glyph/attachment'
import ImageIcon from '@atlaskit/icon/glyph/image'
import WatchIcon from '@atlaskit/icon/glyph/watch'
import DownloadIcon from '@atlaskit/icon/glyph/download'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import { formatFileSize, formatRelativeTime, isImageFile } from '../../utils/formatters'
import { getUserDetails, getCachedUserDetails } from '../../utils/userCache'
import { ConfirmDeleteModal } from '../FolderBrowser/ConfirmDeleteModal'

/**
 * ATTACHMENT ITEM COMPONENT
 *
 * Displays a single attachment with file info and action buttons.
 *
 * Features:
 * - File type icon (image vs generic)
 * - File name, size, uploader, and timestamp
 * - Preview button for images
 * - Download button
 * - Delete button
 *
 * @param {object} attachment - Attachment object from API
 * @param {function} onPreview - Callback for preview action (receives attachment)
 * @param {function} onDownload - Callback for download action (receives attachment)
 * @param {function} onDelete - Callback for delete action (receives attachmentId)
 */
export function AttachmentItem({
  attachment,
  onPreview,
  onDownload,
  onDelete
}) {
  // State for user details (uploader)
  const [userDetails, setUserDetails] = useState(() => {
    // Try to get from cache first for immediate render
    return getCachedUserDetails(attachment?.createdBy)
  })

  // State for loading/deleting
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  // State for the Jira-styled delete confirmation modal.
  // We use ConfirmDeleteModal (same component used for test case/step delete)
  // instead of window.confirm so the dialog matches the rest of the app.
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  // Extract uploader accountId from attachment
  const uploaderAccountId = attachment?.createdBy

  /**
   * FETCH USER DETAILS
   * Fetches uploader details from Jira API on mount.
   */
  useEffect(() => {
    if (uploaderAccountId) {
      getUserDetails(uploaderAccountId).then(details => {
        if (details) {
          setUserDetails(details)
        }
      })
    }
  }, [uploaderAccountId])

  /**
   * HANDLE PREVIEW
   * Opens the preview modal/lightbox for images.
   */
  const handlePreview = useCallback(() => {
    if (onPreview) {
      onPreview(attachment)
    }
  }, [attachment, onPreview])

  /**
   * HANDLE DOWNLOAD
   * Downloads the file.
   */
  const handleDownload = useCallback(async () => {
    if (!onDownload) return

    setIsDownloading(true)
    try {
      await onDownload(attachment)
    } catch (error) {
      console.error('Failed to download attachment:', error)
    } finally {
      setIsDownloading(false)
    }
  }, [attachment, onDownload])

  /**
   * HANDLE DELETE CLICK
   * Opens the Jira-styled confirmation modal. The actual deletion is
   * performed by handleConfirmDelete once the user confirms in the modal.
   */
  const handleDeleteClick = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])

  /**
   * HANDLE CONFIRM DELETE
   * Called by ConfirmDeleteModal's onConfirm after the user clicks Delete.
   * ConfirmDeleteModal already wraps this call in try/catch, drives its own
   * button loading state, closes itself on success, and surfaces errors via
   * its built-in notification — so we only need to perform the delete and
   * let any error propagate back up to the modal.
   */
  const handleConfirmDelete = useCallback(async () => {
    await onDelete(attachment.id)
  }, [attachment.id, onDelete])

  // Show loading state if deleting
  if (isDeleting) {
    return (
      <div
        className="attachment-item"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px',
          borderRadius: '4px',
          backgroundColor: token('color.background.neutral', '#F4F5F7'),
          marginBottom: '8px',
          opacity: 0.5,
          minHeight: '48px'
        }}
      >
        <Spinner size="small" />
        <span style={{ marginLeft: '8px', color: token('color.text.subtle') }}>Deleting...</span>
      </div>
    )
  }

  // Determine if file is an image (for preview button)
  const canPreview = isImageFile(attachment.contentType)

  // Determine uploader display name
  const uploaderName = userDetails?.displayName || 'Unknown'

  return (
    <div
      className="attachment-item"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        borderRadius: '4px',
        backgroundColor: token('color.background.neutral', '#F4F5F7'),
        marginBottom: '8px'
      }}
    >
      {/* Left: File icon + Info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
        {/* File type icon */}
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            color: canPreview
              ? token('color.icon.accent.blue', '#0052CC')
              : token('color.icon.subtle', '#6B778C')
          }}
        >
          {canPreview ? (
            <ImageIcon size="medium" label="Image" />
          ) : (
            <AttachmentIcon size="medium" label="File" />
          )}
        </span>

        {/* File info */}
        <div style={{ minWidth: 0, flex: 1 }}>
          {/* File name - truncate if too long */}
          <div
            style={{
              fontSize: '13px',
              fontWeight: 500,
              color: token('color.text', '#172B4D'),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={attachment.fileName}
          >
            {attachment.fileName}
          </div>

          {/* File size + uploader + time */}
          <div
            style={{
              fontSize: '11px',
              color: token('color.text.subtle', '#5E6C84'),
              marginTop: '2px'
            }}
          >
            {formatFileSize(attachment.fileSize)}
            {uploaderName && (
              <>
                {' · '}
                {uploaderName}
              </>
            )}
            {attachment.createdOn && (
              <>
                {' · '}
                {formatRelativeTime(attachment.createdOn)}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right: Action buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
        {/* Preview button - only for images */}
        {canPreview && onPreview && (
          <Tooltip content="Preview">
            <Button
              appearance="subtle"
              spacing="compact"
              onClick={handlePreview}
              iconBefore={<WatchIcon size="small" label="Preview" />}
            />
          </Tooltip>
        )}

        {/* Download button */}
        {onDownload && (
          <Tooltip content="Download">
            <Button
              appearance="subtle"
              spacing="compact"
              onClick={handleDownload}
              isLoading={isDownloading}
              iconBefore={!isDownloading && <DownloadIcon size="small" label="Download" />}
            />
          </Tooltip>
        )}

        {/* Delete button */}
        {onDelete && (
          <Tooltip content="Delete">
            <Button
              appearance="subtle"
              spacing="compact"
              onClick={handleDeleteClick}
              iconBefore={<CrossIcon size="small" label="Delete" />}
            />
          </Tooltip>
        )}
      </div>

      {/* Jira-styled delete confirmation modal — replaces window.confirm so the
          attachment delete flow matches test case / step / folder delete UX. */}
      <ConfirmDeleteModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Attachment"
        itemName={attachment.fileName}
        itemType="attachment"
        warningMessage="This attachment will be permanently deleted. This action cannot be undone."
      />
    </div>
  )
}

export default AttachmentItem
